import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';
import { map, switchMap, takeWhile } from 'rxjs/operators';
import { CohortHelpers } from '../../cohort.helpers';

enum DISPLAY_TYPE {
  COUNTDOWN,
  SCHEDULED,
  JOIN_LINK,
  ERROR,
}

@Component({
  selector: 'lp2-cohort-tile-join',
  templateUrl: './cohort-tile-join.component.html',
  styleUrls: ['./cohort-tile-join.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CohortTileJoinComponent implements AfterViewInit, OnChanges {
  @Input() public joinLink: string;
  @Input() public classBegin: number;
  @Input() public msCountdownThreshold: number;

  @Input() public set msToClassBegin(value: number) {
    this._msToClassBegin = value;
    this.msToClassBegin$.next(value);
  }

  public get msToClassBegin(): number {
    return this.msToClassBegin$.getValue();
  }

  @Output() public joinClicked = new EventEmitter<void>();

  @ViewChild('joinLinkTemplate', { read: TemplateRef }) private joinLinkTemplate: TemplateRef<HTMLElement>;
  @ViewChild('countdownTemplate', { read: TemplateRef }) private countdownTemplate: TemplateRef<HTMLElement>;
  @ViewChild('scheduledTemplate', { read: TemplateRef }) private scheduledTemplate: TemplateRef<HTMLElement>;
  @ViewChild('errorTemplate', { read: TemplateRef }) private errorTemplate: TemplateRef<HTMLElement>;

  private get templateOutletMap(): Record<DISPLAY_TYPE, TemplateRef<HTMLElement>> {
    return {
      [DISPLAY_TYPE.JOIN_LINK]: this.joinLinkTemplate,
      [DISPLAY_TYPE.COUNTDOWN]: this.countdownTemplate,
      [DISPLAY_TYPE.SCHEDULED]: this.scheduledTemplate,
      [DISPLAY_TYPE.ERROR]: this.errorTemplate,
    };
  }

  public templateOutlet: TemplateRef<HTMLElement>;

  private _msToClassBegin: number = 0;
  private isViewInitialized: boolean = false;
  private msToClassBegin$ = new BehaviorSubject<number>(this._msToClassBegin);

  public countdownDisplay$ = this.msToClassBegin$.pipe(
    switchMap((msToClassBegin) =>
      timer(0, 1000).pipe(
        map((_, iteration) => {
          const msRemaining = msToClassBegin - CohortHelpers.getSecondsInMs(iteration);
          this.cdr.markForCheck();
          return msRemaining;
        }),
        takeWhile((msRemaining) => msRemaining >= 0),
        map((msRemaining) => this.getClassBeginCountdown(msRemaining))
      )
    )
  );

  private displayType: DISPLAY_TYPE;

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges) {
    if ('msToClassBegin' in changes || 'joinLink' in changes) {
      this.setDisplayType();
      if (this.isViewInitialized) {
        this.setTemplateOutlet();
      }
    }
  }

  public ngAfterViewInit() {
    this.isViewInitialized = true;
    this.setTemplateOutlet();
  }

  public onJoinClick() {
    this.joinClicked.emit();
  }

  private setDisplayType() {
    const hasJoinLink = Boolean(this.joinLink);
    const isClassBeginningWithinThreshold =
      this.msToClassBegin >= 0 && this.msToClassBegin <= this.msCountdownThreshold;
    const isClassBeginningBeyondThreshold = this.msToClassBegin >= this.msCountdownThreshold;
    const noClassroomURL = this.msToClassBegin < 0 && !hasJoinLink;

    const rules = [
      { when: hasJoinLink, then: DISPLAY_TYPE.JOIN_LINK },
      { when: isClassBeginningWithinThreshold && !hasJoinLink, then: DISPLAY_TYPE.COUNTDOWN },
      { when: isClassBeginningBeyondThreshold && !hasJoinLink, then: DISPLAY_TYPE.SCHEDULED },
      { when: noClassroomURL, then: DISPLAY_TYPE.ERROR },
    ];

    const displayType = rules.find((rule) => rule.when).then;
    this.displayType = displayType;
  }

  private setTemplateOutlet() {
    this.templateOutlet = this.templateOutletMap[this.displayType];
    this.cdr.detectChanges();
  }

  private getClassBeginCountdown(msRemaining: number) {
    const hours = ('0' + CohortHelpers.getHoursFromMs(msRemaining)).slice(-2);
    const minutes = ('0' + CohortHelpers.getMinutesFromMs(msRemaining)).slice(-2);
    const seconds = ('0' + CohortHelpers.getSecondsFromMs(msRemaining)).slice(-2);
    return `${hours}:${minutes}:${seconds}`;
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/cohort/cohort-tile/cohort-tile-join/cohort-tile-join.component.ts